import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaWallet, FaChartPie, FaMoneyBill, FaShoppingBag, FaUtensils } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, onSnapshot, doc, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import './Summary.css';

const FinancesSummary = () => {
  const [stats, setStats] = useState({
    budget: {
      total: 0,
      spent: 0
    },
    topCategories: []
  });
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Subscribe to monthly income
    const userDocRef = doc(db, 'users', user.uid);
    const unsubIncome = onSnapshot(userDocRef, (doc) => {
      if (doc.exists() && doc.data().monthlyIncome !== undefined) {
        setStats(prev => ({
          ...prev,
          budget: {
            ...prev.budget,
            total: doc.data().monthlyIncome
          }
        }));
      }
    });

    // Subscribe to transactions
    const transactionsRef = collection(db, 'users', user.uid, 'transactions');
    const transactionsQuery = query(
      transactionsRef,
      where('type', '==', 'expense'),
      orderBy('date', 'desc')
    );

    const unsubTransactions = onSnapshot(transactionsQuery, (snapshot) => {
      const transactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate total spent
      const totalSpent = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);

      // Calculate top spending categories
      const categoryTotals = transactions.reduce((acc, t) => {
        if (!acc[t.category]) acc[t.category] = 0;
        acc[t.category] += t.amount || 0;
        return acc;
      }, {});

      const topCategories = Object.entries(categoryTotals)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 2)
        .map(([name, amount]) => ({
          name,
          amount,
          icon: name.toLowerCase().includes('food') || name.toLowerCase().includes('dining') 
            ? <FaUtensils /> 
            : <FaShoppingBag />
        }));

      setStats(prev => ({
        ...prev,
        budget: {
          ...prev.budget,
          spent: totalSpent
        },
        topCategories
      }));
    });

    return () => {
      unsubIncome();
      unsubTransactions();
    };
  }, [user]);

  const remainingBudget = stats.budget.total - stats.budget.spent;

  return (
    <div className="summary-card">
      <div className="summary-header">
        <h2>Finances</h2>
        <FaWallet className="summary-icon" />
      </div>

      <div className="summary-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <FaChartPie />
          </div>
          <div className="stat-info">
            <span className="stat-value">${remainingBudget.toFixed(2)}</span>
            <span className="stat-label">Remaining</span>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon">
            <FaMoneyBill />
          </div>
          <div className="stat-info">
            <span className="stat-value">${stats.budget.spent.toFixed(2)}</span>
            <span className="stat-label">Spent</span>
          </div>
        </div>
      </div>

      <div className="summary-stats">
        {stats.topCategories.map((category, index) => (
          <motion.div 
            key={category.name}
            className="stat-item"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="stat-icon">
              {category.icon}
            </div>
            <div className="stat-info">
              <span className="stat-value">${category.amount.toFixed(2)}</span>
              <span className="stat-label">{category.name}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default FinancesSummary; 